import React, { useState, useRef, useEffect } from 'react';
import ModalWrapper from '../../ModalWrapper';
import { createApp, updateApp } from '../../../utils/api/app_apis';
import { STATUS } from '../../../utils/api/sharedfunction';
import { useSnackbar } from '../../../utils/useSnackbar';
import { FormSchemas, FormWrapper, CommonFooter } from '../../FormComponent';
import { PREDEFINED_APPS, getAppByName } from '../../../constants/appConstants';

const AppModal = ({ 
  open, 
  onClose, 
  onSuccess, 
  isEdit = false, 
  initialValues = {}, 
  existingApps = [] 
}) => {
  const { showSnackbar } = useSnackbar();
  const [modalError, setModalError] = useState('');
  const [selectedAppName, setSelectedAppName] = useState('');
  const formRef = useRef(null);

  // Clear errors when modal opens
  useEffect(() => {
    if (open) {
      setModalError('');
      setSelectedAppName('');
    }
  }, [open]);

  // Watch for name field changes and auto-populate (works for both create and edit)
  useEffect(() => {
    if (formRef.current && open) {
      const subscription = formRef.current.watch((value, { name: fieldName }) => {
        if (fieldName === 'name' && value.name) {
          const predefinedApp = getAppByName(value.name);
          if (predefinedApp && value.name !== selectedAppName) {
            setSelectedAppName(value.name);
            formRef.current.setValue('slug_name', predefinedApp.slugName);
            formRef.current.setValue('description', predefinedApp.description);
            formRef.current.setValue('image_url', predefinedApp.imageUrl);
          }
        }
      });

      return () => subscription?.unsubscribe();
    }
  }, [open, selectedAppName]);

  const handleFormSubmit = async () => {
    if (formRef.current) {
      await formRef.current.submit();
    }
  };

  const handleConfirmSubmit = async (formValues) => {
    setModalError('');

    try {
      if (isEdit) {
        const response = await updateApp({
          id: initialValues.id,
          name: formValues.name,
          slug_name: formValues.slug_name,
          image_url: formValues.image_url || null,
          description: formValues.description || null,
        });

        if (response.status === STATUS.SUCCESS) {
          showSnackbar(response.message || 'App updated successfully', 'success');
          if (onSuccess) onSuccess(response.data);
          onClose();
        } else {
          setModalError(response.error || 'Failed to update app');
        }
      } else {
        const response = await createApp({
          name: formValues.name,
          slug_name: formValues.slug_name,
          image_url: formValues.image_url || null,
          description: formValues.description || null,
        });

        if (response.status === STATUS.SUCCESS) {
          showSnackbar(response.message || 'App created successfully', 'success');
          if (onSuccess) onSuccess(response.data);
          onClose();
        } else {
          setModalError(response.error || 'Failed to create app');
        }
      }
    } catch (error) {
      console.error('Error in form submission:', error);
      setModalError(error?.message || (isEdit ? 'Failed to update app' : 'Failed to create app'));
    }
  };

  const fields = [
    {
      name: 'name',
      type: 'select',
      label: 'App Name',
      required: true,
      autoFocus: true,
      options: PREDEFINED_APPS.map(app => ({
        value: app.name,
        label: app.name
      })),
    },
    {
      name: 'slug_name',
      type: 'text',
      label: 'Slug Name',
      required: true,
      disabled: true,
    },
    {
      name: 'image_url',
      type: 'text',
      label: 'Image URL',
      required: false,
    },
    {
      name: 'description',
      type: 'text',
      label: 'Description',
      required: false,
      multiline: true,
      disabled: true,
      rows: 3,
    },
  ];

  const buttons = [
    {
      label: 'Cancel',
      variant: 'outline',
      className: 'primary',
      onClick: onClose,
    },
    {
      label: isEdit ? 'Save' : 'Create',
      variant: 'primary',
      className: 'white bg-blue600',
      onClick: handleFormSubmit,
    },
  ];

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      title={isEdit ? 'Edit App' : 'Create New App'}
      size="small"
      footer={
        <CommonFooter
          buttons={buttons}
          align="right"
        />
      }
    >
      {modalError && (
        <div style={{ color: 'red', marginBottom: 16, padding: '8px 12px', backgroundColor: '#fee', borderRadius: 4 }}>
          {modalError}
        </div>
      )}
      <FormWrapper
        ref={formRef}
        schema={FormSchemas.appForm(existingApps, isEdit, initialValues)}
        fields={fields}
        onSubmit={handleConfirmSubmit}
        defaultValues={
          isEdit
            ? {
                name: initialValues.name || '',
                slug_name: initialValues.slug_name || '',
                image_url: initialValues.image_url || '',
                description: initialValues.description || '',
              }
            : {
                name: '',
                slug_name: '',
                image_url: '',
                description: '',
              }
        }
      />
    </ModalWrapper>
  );
};

export default AppModal;
